import React, { useEffect, useState } from 'react';
import {
  GDSStyleObject,
  Card,
  Box,
  ColumnLayout,
  Column,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { getAccessToken } from '@leagueplatform/engen-capability-shared-library/src/utils/api';
import { DynamicModal } from '@leagueplatform/engen-capability-shared-library/src/components/layouts/dynamicModal';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { easyPlanFAQ } from '../easy-plan-faq.config';
import { PageHeader } from './pages/page-header.component';
import { EasyPlanAccordionComponent } from './pages/easy-plan-accordion.component';
import * as constants from '../common/constant';
import { EasyPlanFAQInfo } from '../types/faq-data-type.types';

export function EasyPlanFAQComponent() {
  const [faqApiFailure, setFaqApiFailure] = useState<boolean>(false);
  const [faqInfoLoading, setFaqInfoLoading] = useState<boolean>(false);

  const [faqInformation, setFaqInformation] = useState<EasyPlanFAQInfo>();
  const config = easyPlanFAQ.getConfig();
  const highmarkURL = config.HIGHMARK_API_URL;
  const faqInfoURL = `${highmarkURL}/digital/v3/members/benefits/easy-plan-faq`;
  const navigate = useNavigate();
  const getFaqInformation = (url: string) => {
    setFaqInfoLoading(true);
    getAccessToken(url)
      .then((result: EasyPlanFAQInfo) => {
        if (result?.easyPlanFaq) {
          setFaqInformation(result);
          setFaqInfoLoading(false);
          setFaqApiFailure(false);
        } else if (result?.errMsg) {
          setFaqInfoLoading(false);
          setFaqApiFailure(true);
        }
      })
      .catch(() => {
        setFaqApiFailure(true);
        setFaqInfoLoading(false);
      });
  };

  useEffect(() => {
    getFaqInformation(faqInfoURL);
  }, [faqInfoURL]);

  const closeAPIFailureModal = () => {
    navigate(-1);
    setFaqApiFailure(false);
  };

  return (
    <Box
      css={
        {
          backgroundColor: '$surfaceBackgroundPrimary',
          minHeight: '100vh',
        } as GDSStyleObject
      }
      data-testid="easyPlanFAQ"
    >
      <PageHeader showBackButton />

      <Card.Flat>
        <Card.Section
          css={
            {
              boxSizing: 'border-box',
              margin: '0px auto',
              width: '100%',
              maxWidth: '1215px',
              padding: '$none',
              '@desktop': {
                marginTop: '44px',
                marginBottom: '79px',
              },
              '@mobile': { width: '100vw', padding: '$none' },
            } as GDSStyleObject
          }
        >
          <StackLayout
            orientation="horizontal"
            spacing={{ '@desktop': '$two' }}
            css={{ display: 'block', padding: '$none' }}
          >
            {faqInfoLoading && (
              <Box
                css={
                  {
                    display: 'flex',
                    marginTop: '200px',
                    flexDirection: 'column',
                    padding: '$half',
                    height: '80px',
                    textAlign: 'center',
                    '@mobile': { flexDirection: 'column' },
                    '@tablet': { flexDirection: 'column' },
                    '@mobileLandscape': { flexDirection: 'column' },
                  } as GDSStyleObject
                }
              >
                <LoadingIndicator data-testid="faqLoadingPanel" />
              </Box>
            )}
            <Box
              css={
                {
                  display: 'flex',
                  marginTop: '0px',
                  width: '100%',
                  flexDirection: 'row',
                  padding: '$none',

                  '@mobile': { flexDirection: 'column' },
                  '@tablet': { flexDirection: 'column' },
                  '@mobileLandscape': { flexDirection: 'column' },
                } as GDSStyleObject
              }
            >
              <ColumnLayout
                background="primary"
                css={{
                  marginBottom: '32px',
                  '@desktop': {
                    width: '1215px',
                  },
                  '@mobile': {
                    paddingInlineStart: '1px',
                    paddingInlineEnd: '1px',
                  },
                }}
              >
                <Column
                  width={{
                    '@initial': 8,
                    '@mobile': 4,
                    '@mobileLandscape': 5,
                    '@tablet': 5,
                  }}
                >
                  <EasyPlanAccordionComponent
                    faqInfoLoading={faqInfoLoading}
                    faqApiFailure={faqApiFailure}
                    faqInformation={faqInformation}
                  />
                </Column>
                <Column
                  width={{
                    '@initial': 4,
                    '@mobile': 4,
                    '@mobileLandscape': 5,
                    '@tablet': 3,
                  }}
                  css={{
                    '@desktop': {
                      padding: '$threeQuarters',
                    },
                    '@mobile': {
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    },
                    '@mobileLandscape': {
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    },
                  }}
                />
              </ColumnLayout>
            </Box>
          </StackLayout>
        </Card.Section>
      </Card.Flat>
      {!faqInfoLoading && faqApiFailure && (
        <DynamicModal
          open={faqApiFailure}
          type="API_FAILED"
          heading={constants.API_FAILED_TITLE}
          description={constants.API_FAILED_CONTENT}
          description2={constants.API_FAILED_CONTENTSECOND}
          saveBtn={constants.OK}
          onClose={closeAPIFailureModal}
          showDiffEmailBtn
        />
      )}
    </Box>
  );
}
